<template>
  <b-row>
    <b-col
      cols="12"
      class="d-flex justify-content-end"
    >
      <b-button @click="ShowSubmitEvaluation = !ShowSubmitEvaluation">
        Submit Evaluation
      </b-button>

      <b-modal
        v-model="ShowSubmitEvaluation"
        footer-class="d-none"
        title="Submit Evaluation"
      >
        <validation-observer
          ref="infoRules"
          v-slot="{ invalid }"
          tag="form"
        >
          <div>
            <b-form-group
              label="CE Level Before"
              label-for="ce_level_before"
            >
              <v-select
                id="ce_level_before"
                v-model="submitEvaluationForm.ce_level_before"
                :options="optionsCertificateStatus"
                :reduce="val => val.value"
                multiple
                placeholder="Select Competence Levels Before"
                :clearable="true"
              />
            </b-form-group>

            <b-form-group
              label="CE Level After"
              label-for="ce_level_after"
            >
              <v-select
                id="ce_level_after"
                v-model="submitEvaluationForm.ce_level_after"
                :options="optionsCertificateStatus"
                :reduce="val => val.value"
                multiple
                placeholder="Select Competence Levels After"
                :clearable="true"
              />
              <b-alert
                v-if="isDowngrade"
                variant="danger"
                show
                class="mt-1 mb-0 p-1"
              >
                <div class="alert-body d-flex align-items-center">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  <small>
                    <strong>Warning:</strong> The selected level is lower than the current level ({{ previousLevelText }}). This will downgrade the engineer.
                  </small>
                </div>
              </b-alert>
            </b-form-group>
            <b-form-group
              label="Conclusion"
              label-for="conclusion"
            >
              <b-form-input
                id="conclusion"
                v-model="submitEvaluationForm.conclusion"
                trim
              />
            </b-form-group>

            <b-form-group
              label="Personnel Evaluation Remarks"
              label-for="personnel_evaluation_remarks"
            >
              <b-form-input
                id="personnel_evaluation_remarks"
                v-model="submitEvaluationForm.personnel_evaluation_remarks"
              />
            </b-form-group>

            <b-form-group
              label="Next Evaluation Date"
              label-for="next_evaluation_date"
            >
              <b-form-input
                id="next_evaluation_date"
                v-model="submitEvaluationForm.next_evaluation_date"
                type="date"
              />
            </b-form-group>
            <b-button
              v-if="!loader"
              :disabled="invalid || Object.values(errors).length > 0"
              @click="submitEvaluation"
            >
              Submit
            </b-button>
            <b-button
              v-if="loader"
              variant="primary"
              disabled
              class="mr-1"
            >
              <b-spinner
                small
                type="grow"
              />
              Loading...
            </b-button>
          </div>
        </validation-observer>
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
import { required } from '@validations'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    vSelect,
  },
  props: {
    evaluationId: {
      type: [Number, String],
      required: true,
    },
    evaluationData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      ShowSubmitEvaluation: false,
      loader: false,
      required,
      errors: {},
      submitEvaluationForm: {
        conclusion: '',
        personnel_evaluation_remarks: '',
        next_evaluation_date: '',
        ce_level_before: [],
        ce_level_after: [],
      },
    }
  },
  computed: {
    optionsCertificateStatus() {
      return this.$store.getters['generalData/certificateStatus/certificateStatus']
    },
    isDowngrade() {
      const ceLevels = Array.isArray(this.submitEvaluationForm.ce_level_after)
        ? this.submitEvaluationForm.ce_level_after
        : []
      const primaryLevel = ceLevels.length > 0 ? Math.max(...ceLevels) : null

      const minLevelId = this.evaluationData?.ce_level_before?.id
        || this.evaluationData?.ce_level_before
        || 1

      if (primaryLevel !== null && primaryLevel < minLevelId) {
        return true
      }
      return false
    },
    previousLevelText() {
      return this.evaluationData?.ce_level_before?.status || `Level ${this.evaluationData?.ce_level_before || 1}`
    },
  },
  watch: {
    ShowSubmitEvaluation(val) {
      if (val && this.evaluationData) {
        if (Array.isArray(this.evaluationData.ce_levels_before) && this.evaluationData.ce_levels_before.length) {
          this.submitEvaluationForm.ce_level_before = [...this.evaluationData.ce_levels_before]
        } else if (this.evaluationData.ce_level_before) {
          const bId = typeof this.evaluationData.ce_level_before === 'object' ? this.evaluationData.ce_level_before.id : this.evaluationData.ce_level_before
          this.submitEvaluationForm.ce_level_before = [bId]
        }
      }
    },
  },
  async mounted() {
    if (!this.$store.getters['generalData/certificateStatus/certificateStatusLoaded']) {
      await this.$store.dispatch('generalData/certificateStatus/fetchCertificateStatus')
    }
  },
  methods: {
    submitEvaluation() {
      this.loader = true
      const ceLevelsAfter = Array.isArray(this.submitEvaluationForm.ce_level_after)
        ? this.submitEvaluationForm.ce_level_after
        : []
      const primaryLevelAfter = ceLevelsAfter.length > 0 ? Math.max(...ceLevelsAfter) : null

      const ceLevelsBefore = Array.isArray(this.submitEvaluationForm.ce_level_before)
        ? this.submitEvaluationForm.ce_level_before
        : []
      const primaryLevelBefore = ceLevelsBefore.length > 0 ? Math.max(...ceLevelsBefore) : null

      const payloadTO = {
        ce_levels_before: ceLevelsBefore,
        ce_level_before: primaryLevelBefore,
        ce_levels_after: ceLevelsAfter,
        ce_level_after: primaryLevelAfter,
        conclusion: this.submitEvaluationForm.conclusion,
        personnel_evaluation_remarks: this.submitEvaluationForm.personnel_evaluation_remarks,
        next_evaluation_date: this.submitEvaluationForm.next_evaluation_date,
      }

      axios.post(`/personnel-evaluation/${this.evaluationId}/complete`, payloadTO).then(res => {
        if (res.status === 200 || res.status === 201) {
          this.ShowSubmitEvaluation = false
          this.$toasted.show('Created Successfully', {
            position: 'top-center',
            duration: 3000,
          })
          window.location.reload()
        }
      }).catch(error => {
        let errorMessage = 'An unknown error occurred'
        if (error.response && error.response.data) {
          if (error.response.data.payload) {
            try {
              const payload = decryptData(error.response.data.payload)
              if (typeof payload === 'object') {
                const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
                errorMessage = firstArrayKey ? payload[firstArrayKey][0] : payload.message || errorMessage
              }
            } catch (e) {
              errorMessage = error.response.data.message || errorMessage
            }
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'BellIcon',
            variant: 'error',
          },
        })
      }).finally(() => {
        this.loader = false
      })
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
  },
}
</script>
